
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UseDeviceChartDataProps {
  deviceId: string;
  enabled?: boolean;
}

export const useDeviceChartData = ({ deviceId, enabled = true }: UseDeviceChartDataProps) => {
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const triggerRefresh = useCallback(() => {
    console.log(`[Chart Data] Manual refresh triggered for ${deviceId}`);
    setLastUpdate(new Date());
  }, [deviceId]);

  useEffect(() => {
    if (!enabled || !deviceId) return;

    // Unique topic per mount so a channel is never reused after subscribe()
    const topic = `device_chart_${deviceId}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const channel = supabase.channel(topic);

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'led_triggers',
        filter: `device_id=eq.${deviceId}`,
      },
      (payload) => {
        setLastUpdate(new Date());
        if (payload.eventType === 'INSERT' && payload.new) {
          toastRef.current({
            title: 'Motion Detected',
            description: `New activity on ${deviceId}`,
            duration: 2000,
          });
        }
      }
    );

    channel.subscribe((status) => {
      console.log(`[Chart Data] Subscription status for ${deviceId}:`, status);
    });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [deviceId, enabled]);


  // Fallback refresh; skipped while the tab is in the background.
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, [deviceId, enabled]);

  return {
    lastUpdate,
    triggerRefresh
  };
};
